import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";

import PropTypes from "prop-types";

import { MaskedTextBox } from "@progress/kendo-react-inputs";
import { Calendar } from "@progress/kendo-react-dateinputs";
import { TimeSelector } from "@progress/kendo-react-dateinputs/dist/npm/timepicker/TimeSelector";
import { DateTimeSelector } from "@progress/kendo-react-dateinputs/dist/npm/datetimepicker/DateTimeSelector";
import { Popup } from "@progress/kendo-react-popup";

import "./DateInput.scss";

const replaceRange = (str, tok, start, end) => {
  return str.substring(0, start) + tok + str.substring(end);
}

const dateToString = (date, useDate, useTime) => {
  // to locale Date string
  let newdate = useDate
    ? date?.toLocaleDateString("sk-SK").replace(/[ ]/g, "")
    : "";
  if (newdate === undefined) newdate = "";
  let arr = newdate.includes(".") ? newdate.split(".") : newdate.split("/");

  let time = useTime
    ? `${date.toTimeString("sk-SK").substring(0, 5)}`
    : "";
  for (let i = 0; i < arr.length; i++) {
    if (arr[i].length % 2 !== 0) {
      arr[i] = "0" + arr[i];
    }
  }

  return (
    arr.join(".") +
    (useDate && useTime ? " " : "") +
    time
  );
}

export const dateToFormattedString = (date, type, format = null) => {
  if (!date) return null;
  
  const types = ["date", "datetime", "time"];
  if (!types.includes(type)) type = "date";

  const formatGroups = {
    date: ["d", "D", "m", "M", "y", "Y"],
    datetime: ["F", "g", "G", "s", "u"],
    time: ["t", "T"]
  };

  const useTime = type === "datetime" || type === "time" || /[fHms]/g.test(getFormat({format, type}));
  const useDate =  type !== "time" || /[dMy]/g.test(getFormat({format, type}));

  // "dd.MM.yyyy", internal format
  let newStrInternal = dateToString(date, useDate, useTime);
  // use internal string if no format is specicified
  if ([null, undefined].includes(format)) return newStrInternal;

  // fallback to internal string
  if (!formatGroups[type].includes(format) && format.length === 1) return newStrInternal;

  return window.kendo.toString(date, format);
}

export const pickFromStringDate = (value, regex, maxCount) => {
  let start = -1;
  let range = "";

  for (let i = 0; i < value.length; i++) {
    if (regex.test(value[i])) {
      range = range + value[i];
      if (start < 0) start = i;

      if (maxCount) {
        if (range.length > maxCount) {
          start = -1;
          range = "";
        }
      }
    } else if (start > 0) {
      return [start, range.length, range];
    }
  }

  return [start, range.length, range];
}

const doubleChar = (str, char) => {
  for (let i = 0; i < str.length; i++) {
    let before = true;
    let after = true;

    if (str[i] === char) {
      if (i === 0 || str[i - 1] !== char) before = false;
      if (i === str.length - 1 || str[i + 1] !== char) after = false;
    }

    if (!before && !after) return replaceRange(str, char + char, i, i + 1);
  }

  return str;
}

export const getFormatString = (format) => {
  if (!format) return format;

  let str = format.length !== 1 ? format : window.kendo.culture().calendars.standard.patterns[format];

  str = doubleChar(str, "d");
  str = doubleChar(str, "M");
  str = doubleChar(str, "y");
  str = doubleChar(str, "h");
  str = doubleChar(str, "m");
  return doubleChar(str, "s");
}

export const extractFormat = (format) => {
  if (!format) return null;
  format = format.trim();
  if (format[0] !== "{") return format;

  let trueFormat = "";
  let _prevKey = "";
  let start = -1;
  let end = -1;

  for (let i = 0; i < format.length; i++) {
    if (_prevKey === "0" && format[i] === ":") {
      start = i + 1;
      break;
    }

    _prevKey = format[i];
  }
  end = format.indexOf("}");

  // invalid start or end pos
  if (start <= 0 || end < start) return null;
  trueFormat = format.substring(start, end);

  return trueFormat.trim();
}

const defaultDateFormat = "dd.MM.yyyy";
const defaultTimeFormat = "HH:mm";
const defaultDateTimeFormat = "dd.MM.yyyy HH:mm";

// props: format, type
export const getFormat = (props) => {
  const extractedFormat = extractFormat(props.format ?? (
      props.type === "datetime"
        ? defaultDateTimeFormat
        : props.type === "time"
          ? defaultTimeFormat
          : defaultDateFormat));

  const formatUseDate = /[dMy]/g.test(extractedFormat);
  const formatUseTime = /[fHms]/g.test(extractedFormat);
  let chosenFormat = extractedFormat;

  if (props.type === "date" && !formatUseDate) chosenFormat = defaultDateFormat;
  if (props.type === "time" && !formatUseTime) chosenFormat = defaultTimeFormat;
  if (props.type === "datetime" && (!formatUseDate || !formatUseTime)) chosenFormat = defaultDateTimeFormat;

  return getFormatString(chosenFormat);
}

const getMask = (props) => {
  // handle MMM-MMMM, ddd-dddd
  return (getFormatString(getFormat(props))
            .replace(/[fhHmsy]/g, "0")
            .replace("dddd", "LLLL")
            .replace("ddd", "LLL")
            .replace("dd", "00")
            .replace("d", "0")
            .replace("MMMM", "LLLL")
            .replace("MMM", "LLL")
            .replace("MM", "00")
            .replace("M", "0")
            .replace(/[t]/g, "L")
            .replace(/[z]/g, "A")
          );
}

const getStateFromProps = (props) => {
  return {
    // value: props.value instanceof Date ? dateToString(props.value, props.type !== "time", props.type !== "date") : "",
    value: props.value instanceof Date ? dateToFormattedString(props.value, props.type, getFormat(props)) : "",
    date: props.value instanceof Date ? props.value : null,
    valid: false,
    mask: getMask(props),
    format: getFormat(props),
    useTime: props.type === "datetime" || props.type === "time" || /[fHms]/g.test(getFormat(props)),
    useDate: props.type !== "time" || /[dMy]/g.test(getFormat(props)),
    steps: {
      hour: props.steps?.hour,
      minute: props.steps?.minute,
    },
    placeholder:
      props.placeholder ??
      `Zvoľte požadovaný ${props.type === "datetime"
        ? "dátum a čas"
        : props.type === "time"
          ? "čas"
          : "dátum"
      }`,
    tab: 0,
    valueInvalid: false,
  };
}


/**
 * Date picker using hooks
 * @module
 * @param {string} name - Name
 * @param {string} type - date or datetime or time => choose between to types
 * @param {string} [format] - specify custom or kendo format, if null or undefined, default format for type is used
 * @param {func} onChange - Function invoked on value change
 * @param {object} steps - object containing required hour/minute increment
 * @param {Date} [value] - Current input value
 * @param {bool} [disabled] - Is input readonly? => user can not change value
 * @param {bool} [required] - Is input required? => can not be left empty
 * @param {bool} [validationHidden] - Is validation hidden
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {Date} [min] - Minimum date
 * @param {Date} [max] - Maximum date
 * @param {number} [tabIndex] - Tabindex of datepicker => index of input using <tab> to navigate
 * @param {func} [onBlur] - Function invoked on input blur
 *
 *              Date                         Format : format je fixny v tvare "dd.mm.yyyy" !!!!
 *              DateTime                     Format : format je fixny v tvare "dd.mm.yyyy HH:MM" !!!!
 */

export const DateInput = forwardRef((props, ref) => {
  const [state, setState] = useState(getStateFromProps(props));

  const [calendarOpen, setCalendarOpen] = useState(false);

  const anchor = useRef(null);
  const popupRef = useRef(null);
  const popupInsideRef = useRef(null);
  const inputRef = useRef(null);

  // for useEffect event handling
  let eventHolder = useRef(null);
  // to prevent from onchange calling props.onchange
  let noOnChangeCallback = useRef(false);

  let yearAutocompleteWait = useRef(false);

  useImperativeHandle(ref, () => {
    return {
      stringToDate,
      dateToString: dateToStringInternal,
      dateInRange,
      normalise,
      getToday,
      onChange,
      state: {
        value: state.value,
        date: state.date
      }
    };
  });

  const setFromProps = () => {
    setState(getStateFromProps(props));
  }

  useEffect(() => {
    // leftover selection causes issues in grid edit
    if (inputRef.current) inputRef.current._selection = [null, null];
    setFromProps();
    // eslint-disable-next-line
  }, [props.value, props.format]);

  useEffect(() => {
    if ((props.onChange && state.valid) && !noOnChangeCallback.current) {
      props.onChange(props.name, state.date, eventHolder.current);
    }
    noOnChangeCallback.current = false;
    if (eventHolder.current?.type === "click") onClear();

    setCursorToDefaultPos();

    // eslint-disable-next-line
  }, [state.value, state.valid, state.date, state.valueInvalid]);

  useEffect(() => {
    if (calendarOpen)
      popupInsideRef.current.focus();
  }, [calendarOpen])

  const setCursorToDefaultPos = () => {
    if (!state.value) {
      inputRef.current._input.selectionStart = 0;
      inputRef.current._input.selectionEnd = 0;
    }
  }

  const stringToDate = (val, outformat) => {
    if (!val || !val.replace(/[aA-zZ\-.,_ :/]/g, "")) return null;
    if (!validate(val)) return null;

    return window.kendo.parseDate(val, outformat ?? state.format);
  };

  const dateToStringInternal = (date) => {
    return dateToFormattedString(date, props.type, state.format);
  };

  const dateInRange = (date, min, max) => {
    if (!state.useDate) return true;
    if (date === undefined || date === null) return true;
    if (min === undefined || min === null) min = date;
    if (max === undefined || max === null) max = date;

    // careful of time
    if (!state.useTime) {
      min.setHours(0, 0, 0, 0);
      max.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
    }

    return min <= date && date <= max;
  };

  const normalise = (value) => {
    if ([null, undefined].includes(value)) return value;
    return value.toString().length > 1 ? value.toString() : `0${value}`;
  };

  const normaliseTo = (value, to) => {
    if ([null, undefined].includes(value)) return value;
    let res = "";

    for (let i = 0; i < to - value.toString().length; i++) {
      res = res + "0";
    }
    return `${res}${value}`;
  }

  const correctDate = (value) => {
    let newValue = "";
    let day, month, year, hour, minute, second = "";
    const formatString = getFormatString(state.format);

    if (state.useDate) {

      // check year
      const [ yearStart, yearCount ] = pickFromStringDate(formatString, /[yY]/);
      if (yearStart >= 0) {
        if (!(new RegExp(`^.{${yearStart}}([0-9]{${yearCount}})`)).test(value)) return null;
        year = value.substring(yearStart, yearStart + yearCount);
        newValue = replaceRange(value, normaliseTo(year, yearCount), yearStart, yearStart + yearCount);
      }

      // check month
      const [ monthStart, monthCount ] = pickFromStringDate(formatString, /[M]/, 2);
      if (monthStart >= 0) {
        if (!(new RegExp(`^.{${monthStart}}([0-9]{${monthCount}})`)).test(value)) return null;
        month = parseInt(value.substring(monthStart, monthStart + monthCount));
        if (month <= 0) month = 1;
        if (month > 12) month = 12;

        month = normalise(month);
        newValue = replaceRange(newValue === "" ? value : newValue, month, monthStart, monthStart + monthCount);
      }

      // check day
      const [ dayStart, dayCount ] = pickFromStringDate(formatString, /[d]/, 2);
      let d = new Date(year, month, 0);

      if (dayStart >= 0) {
        if (!(new RegExp(`^.{${dayStart}}([0-9]{${dayCount}})`)).test(value)) return null;

        day = parseInt(value.substring(dayStart, dayStart + dayCount));
        if (day <= 0) day = 1;
        if (day > d.getDate()) day = d.getDate();

        day = normalise(day);
        newValue = replaceRange(newValue === "" ? value : newValue, day, dayStart, dayStart + dayCount);
      }

      if (!state.useTime) return newValue;
    }

    // time
    // check hour

    const [ hourStart, hourCount, hourMask ] = pickFromStringDate(formatString, /[hH]/, 2);
    const [ minuteStart, minuteCount ] = pickFromStringDate(formatString, /[m]/, 2);
    const [ secondStart, secondCount ] = pickFromStringDate(formatString, /[s]/, 2);

    if (hourStart >= 0) {
      if (!new RegExp(`^.{${hourStart}}([0-9]{${hourCount}})`).test(value)) return null;
      hour = parseInt(value.substring(hourStart, hourStart + hourCount));
      let maxHour = hourMask.includes("H") ? 24 : 13;

      if (hour < 0) hour = 0;
      if (hour >= maxHour) hour = maxHour - 1;

      hour = normalise(hour);
      newValue = replaceRange(newValue === "" ? value : newValue, hour, hourStart, hourStart + hourCount);
    }

    // minute
    if (minuteStart >= 0) {
      if (!new RegExp(`^.{${minuteStart}}([0-9]{${minuteCount}})`).test(value)) return null;
      minute = parseInt(value.substring(minuteStart, minuteStart + minuteCount));
      if (minute < 0) minute = 0;
      if (minute > 59) minute = 59;

      minute = normalise(minute);
      newValue = replaceRange(newValue === "" ? value : newValue, minute, minuteStart, minuteStart + minuteCount);
    }

    // second
    if (secondStart >= 0) {
      if (!new RegExp(`^.{${secondStart}}([0-9]{${secondCount}})`).test(value)) return null;
      second = parseInt(value.substring(secondStart, secondStart + secondCount));
      if (second < 0) second = 0;
      if (second > 59) second = 59;

      second = normalise(second);
      newValue = replaceRange(newValue === "" ? value : newValue, second, secondStart, secondStart + secondCount);
    }

    return newValue;
  };

  const getToday = () => {
    return dateToStringInternal(new Date());
  };

  const validate = (value) => {
    if (!value) return false;
    if (!value.replace(/[aA-zZ\-.,_ :/]/g, "") || value.includes("_")) return false;
    if (window.kendo.parseDate(value, state.format) !== null) return true;
    return false
  };

  const validateOnlyDate = (value, includeYear = true) => {
    if (!value) return false;
    const formatString = getFormatString(state.format);

    const [ yearStart, yearCount ] = pickFromStringDate(formatString, /[yY]/);
    const validYear = new RegExp(`^.{${yearStart}}([0-9]){${yearCount}}`);

    const [ dayStart ] = pickFromStringDate(formatString, /[d]/, 2);
    const validDay = new RegExp(`^.{${dayStart}}([1-2][0-9])|^.{${dayStart}}(0[1-9])|^.{${dayStart}}30|^.{${dayStart}}31`);

    const [ monthStart ] = pickFromStringDate(formatString, /[M]/, 2);
    const validMonth = new RegExp(`^.{${monthStart}}(0[1-9])|^.{${monthStart}}(1[0-2])`);

    return (
      (includeYear ? validYear.test(value) : true) &&
      (dayStart > 0 ? validDay.test(value) : true) &&
      (monthStart > 0 ? validMonth.test(value) : true)
    );
  };

  // const validateOnlyTime = (value) => {
  //   const formatString = getFormatString(state.format);

  //   const [ hourStart, hourCount, hourMask ] = pickFromStringDate(formatString, /[hH]/, 2);
  //   const validHour = new RegExp(`^.{${hourStart}}([0][0-9])|^.{${hourStart}}([1][0-9])|^.{${hourStart}}([2][0-3])`);
  //   // const validHour = /^.{0}([0][0-9])|^.{0}([1][0-9])|^.{0}([2][0-3])/;

  //   const [ minuteStart, minuteCount, minuteMask ] = pickFromStringDate(formatString, /[m]/, 2);
  //   const validMinute = new RegExp(`^.{${minuteStart}}([0-5][0-9])`);
  //   // const validMinute = /^.{3}([0-5][0-9])/;

  //   const [ secondStart, secondCount, secondMask ] = pickFromStringDate(formatString, /[s]/, 2);
  //   const validSecond = new RegExp(`^.{${secondStart}}([0-5][0-9])`);
  //   // const validSecond = /^.{3}([0-5][0-9])/;

  //   return (
  //     (hourStart > 0 ? validHour.test(value) : true) &&
  //     (minuteStart > 0 ? validMinute.test(value) : true) && 
  //     (secondStart > 0 ? validSecond.test(value) : true)
  //   );
  // };

  const autoComplYear = (value, nativeEvent) => {
    if (!state.useDate || !value) return [value];

    const formatString = getFormatString(state.format);
    const [ yearStart, yearCount ] = pickFromStringDate(formatString, /[yY]/);

    if (value.substring(yearStart, yearStart + yearCount).replace(/[_]/g, "").trim() !== "") return [null];
    if (!validateOnlyDate(value, false) || validate(value)) return [null];

    const newYearValue = new Date().getFullYear();
    let newValue = replaceRange(value, `${newYearValue}`.slice(-yearCount), yearStart, yearStart + yearCount);

    return [newValue, (newValue !== value) ? yearStart : null];
  }

  const autoComplTime = (value, nativeEvent) => {

    const formatString = getFormatString(state.format);
    // get time form value
    let newVal = "";

    const [ hourStart, hourCount ] = pickFromStringDate(formatString, /[hH]/, 2);
    const [ minuteStart, minuteCount ] = pickFromStringDate(formatString, /[m]/, 2);
    const [ secondStart, secondCount ] = pickFromStringDate(formatString, /[s]/, 2);
    
    let start = Math.min(hourStart >= 0 ? hourStart : 9999, minuteStart >= 0 ? minuteStart : 9999, secondStart >= 0 ? secondStart : 9999);
    let end = Math.max(hourStart + hourCount, minuteStart + minuteCount, secondStart + secondCount);

    // order by start ascending and filter out not used items
    const orderedArr = [[hourStart, hourCount], [minuteStart, minuteCount], [secondStart, secondCount]].sort(item => item[0] * -1).filter(item => item[0] >= 0);

    if (start < 0 || end < start) return [value];
    newVal = value.substring(start, end).trim();
    if (!newVal) return [value];

    // move and replace 01:50 to 15:02
    if (validate(state.value)) {
    // if (validate(value)) {

      if (!nativeEvent) return [value];
      if (!/[0-9]/g.test(nativeEvent.data)) return [value];
      
      if (newVal[0] !== "0") return [value];

      newVal = newVal.replace(":", "").replace(/[ _]/g, "");

      if (
        newVal.length < 4 ||
        // make sure you are adding at the end
        inputRef.current._input.selectionStart < end
      )
        return [value];

      newVal = newVal.substring(1) + nativeEvent.data;
      // fill newVal to value: 1235 => date ... 12:35 ... 
      let _ = value;
      for (let i = 0; i < orderedArr.length; i ++) {
        _ = replaceRange(_, newVal.substring(i * 2, i * 2 + 2), orderedArr[i][0], orderedArr[i][0] + orderedArr[i][1]);
      }
      newVal = _;

      if (!validate(newVal)) return [value];

      return [newVal, end];
    }

    // classic autocomplete
    // cut from end and start only and not from middle
    newVal = newVal.replace(":", "").replace(/^_+|_+$/g, "");
    if (newVal.length !== 3) return [value];

    newVal = "0" + newVal;
    let _ = value;
    for (let i = 0; i < orderedArr.length; i ++) {
      _ = replaceRange(_, newVal.substring(i * 2, i * 2 + 2), orderedArr[i][0], orderedArr[i][0] + orderedArr[i][1]);
    }
    newVal = _;

    if (!validate(newVal)) return [value];

    return [newVal, end];
  };

  const roundToClosest = (num, closest = 1) => {
    if (!num) return;
    if (num % closest === 0) return num;

    let newNum = num;
    if ((newNum % closest) >= Math.round(closest / 2)) {
      newNum += closest - (newNum % closest);
    } else {
      newNum -= newNum % closest;
    }

    return newNum;
  }

  const roundTime = (value) => {
    if (!value || !validate(value)) return value;

    let overflowFormMinute = 0;

    const formatString = getFormatString(state.format);

    // round Minute
    let minute = "";
    const [ minuteStart, minuteCount ] = pickFromStringDate(formatString, /[m]/, 2);
    let rangeMinute = { start: minuteStart, end: minuteStart + minuteCount };
    minute = value.substring(rangeMinute.start, rangeMinute.end);

    if (state.steps.minute) {
      let rMinute = roundToClosest(parseInt(minute), state.steps.minute);

      if (rMinute > 59) overflowFormMinute = 1;
      minute = rMinute > 59 ? 0 : rMinute;
    }

    // round Hour 
    let hour = "";
    const [ hourStart, hourCount, hourMask ] = pickFromStringDate(formatString, /[hH]/, 2);
    let rangeHour = { start: hourStart, end: hourStart + hourCount };
    hour = parseInt(value.substring(rangeHour.start, rangeHour.end)) + overflowFormMinute;

    if (state.steps.hour) {
      let rHour = roundToClosest(hour, state.steps.hour);
      hour = rHour;
    }
    let maxHour = hourMask.includes("H") ? 23 : 12;
    hour = hour > maxHour ? 0 : hour;

    // connect
    hour = normalise(hour);
    minute = normalise(minute);
    let newValue = replaceRange(value, hour, hourStart, hourStart + hourCount);
    newValue = replaceRange(newValue, minute, minuteStart, minuteStart + minuteCount);

    return newValue;
  }

  const autoCmpWait = (newValue, oldValue, pos) => {
    clearTimeout(yearAutocompleteWait.current);

    yearAutocompleteWait.current = setTimeout(() => {
      if (oldValue !== inputRef.current.state.value) return;

      const valid = validate(newValue);
      let date = undefined;
      if (valid) date = stringToDate(newValue);

      setState({
        ...state,
        value: newValue,
        valid: valid,
        date: date,
        valueInvalid: !(
          valid && dateInRange(date, props.min, props.max)
        ),
      });

      inputRef.current._selection = [
        pos ?? inputRef.current._selection[0],
        pos ?? inputRef.current._selection[1],
      ];
    }, 350);
  }

  const onChange = (e) => {
    eventHolder.current = e;
    let value = e?.value;
    let correctedValue = correctDate(value);
    if (correctedValue !== null) value = correctedValue;

    let pos = state.mask.replace(/[0LA]/g, "_") === value ? 0 : null;
    let newPos = null;
    let newPosFromYear = null;

    if (state.useTime) {
      [value, newPos] = autoComplTime(value, e?.nativeEvent);
    }

    let autocmpValue = null;
    [autocmpValue, newPosFromYear] = autoComplYear(value, e?.nativeEvent);
    // warn: has to be after all autocompletes / value edits
    if (autocmpValue !== value && autocmpValue) autoCmpWait(autocmpValue, value, newPosFromYear);
    newPosFromYear = null;

    pos = newPos ?? newPosFromYear ?? pos;

    // nemoze byt tu lebo si navzajom s autocomplete prepisuju hodnoty
    // if (validate(value) && state.useTime) value = roundTime(value);

    const valid = validate(value);

    let date = undefined;
    if (valid) date = stringToDate(value);

    setState({
      ...state,
      value: value,
      valid: valid,
      date: date,
      valueInvalid: !(
        valid && dateInRange(date, props.min, props.max)
      ),
    });

    inputRef.current._selection = [
      pos ?? inputRef.current._selection[0],
      pos ?? inputRef.current._selection[1],
    ];
  };

  const onClear = () => {
    // leftover selection causes issues in grid edit
    inputRef.current._selection = [null, null];
    if (props.onClear) props.onClear(state.value);
  }

  const onMaskedTBFocus = () => {
    // leftover selection causes issues in grid edit
    inputRef.current._selection = [null, null];

    if (!state.value) setState({
      ...state,
      value: state.mask.replace(/[0LA]/g, "_")
    });
  }

  // const selectTab = (val) => {
  //   setState({
  //     ...state,
  //     tab: val.selected,
  //   });
  // };

  const onBlur = (e) => {
    // leftover selection causes issues in grid edit
    inputRef.current._selection = [null, null];

    /*if (!state.calendarOpen) {
      onChange({
        value: state.value,
      });
    }*/

    // tu treba zavolat onchange lebo sa opravuje datum podla step, inak neopravuje detailData
    let value;
    if (validate(state.value) && state.useTime) value = roundTime(state.value);
    if (value && (state.steps.hour !== undefined || state.steps.minute !== undefined)) onChange({ value });

    clearTimeout(yearAutocompleteWait.current);
    if (props.onBlur) props.onBlur(e);
  };

  const cancelClosePopup = () => {
    clearTimeout(popupRef.current);
  };

  const closePopup = () => {
    clearTimeout(popupRef.current);
    popupRef.current = setTimeout(() => {
      setCalendarOpen(false);
    }, 200);
  };

  const onChangePopup = (e) => {
    if (!e) return;
    if (!e.value) return;

    onChange({ ...e, value: dateToStringInternal(e.value) });
    closePopup();
  };

  const buildPopUpContent = () => {
    if (state.useDate && !state.useTime) {
      return (
        <Calendar
          onChange={onChangePopup}
          min={props.min ?? undefined}
          max={props.max ?? undefined}
          value={stringToDate(state.value ?? "")}
        />
      );
    } else if (!state.useDate && state.useTime) {
      return (
        <TimeSelector
          onChange={onChangePopup}
          value={state.date}
          cancelButton={false}
          steps={state.steps}
        />
      );
    } else {
      return (
        <DateTimeSelector
          min={props.min ?? undefined}
          max={props.max ?? undefined}
          value={stringToDate(state.value ?? "")}
          onChange={onChangePopup}
          onReject={() => closePopup()}
          cancelButton={true}
          format={state.format}
          steps={state.steps}
        />
      );
    }
  };

  return (
    <>
      <div className="k-dateinput-wrap wrap">
        <span
          className={`form-input dpop k-input k-input-md k-input-solid k-datepicker datepicker ${props.disabled ? "disabled-field" : props.required ? "required-field" : "default-field"} ${state.valueInvalid ? "dpop-invalid" : ""}`}
          onFocus={props.onFocus}
          ref={anchor}
          onBlur={onBlur}
        >
          <MaskedTextBox
            ref={inputRef}
            className={"k-dateinput k-input masked-tb " + (props.className ?? "")}
            includeLiterals={true}
            name={props.name}
            valid={!state.valueInvalid || props.disabled}
            mask={state.mask}
            placeholder={
              props.disabled
                ? ""
                : props.placeholder || state.placeholder
            }
            value={state.value}
            onChange={onChange}
            onFocus={onMaskedTBFocus}
            disabled={props.disabled}
            validityStyles={null}
            required={props.required}
            tabIndex={props.tabIndex}
          >
          </MaskedTextBox>
          {!props.disabled &&
            <div className={`k-icon k-i-close datepicker-reset${(props.disabled || !state.value) ? '-disabled' : ''} ${!props.todayHidden ? 'today' : ''} ${props.nobuttons ? 'dateinput-nobuttons' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                inputRef.current._selection = [0, 0];
                onChange({ syntheticEvent: e, nativeEvent: e.nativeEvent, target: e.target, value: "" });
                anchor?.current?.focus();
              }}
            ></div>
          }

          {props.nobuttons ? (
            <></>
          ) : (
            <>
              <div
                className={`but k-icon ${state.useDate ? `k-i-calendar${props.disabled ? "-disabled" : ""}` : `k-i-clock${props.disabled ? "-disabled" : ""}`}  datepicker-open`}
                onClick={(e) => {
                  e.preventDefault();
                  setCalendarOpen(!calendarOpen);
                }}
                disabled={props.disabled}
              />
              {!props.todayHidden && state.useDate && (
                <div
                  className={`but but-primary k-icon k-i-calendar-date${props.disabled ? "-disabled" : ""} datepicker-today`}
                  onClick={(e) => {
                    e.preventDefault();
                    let today = getToday();
                    onChange({ ...e, value: today });
                  }}
                  disabled={props.disabled}
                />
              )}
            </>
          )}

          <Popup
            anchor={anchor.current}
            show={calendarOpen}
            ref={popupRef}
          >
            <div
              tabIndex="0"
              onBlur={closePopup}
              onFocus={cancelClosePopup}
              ref={popupInsideRef}
            >
              {buildPopUpContent()}
            </div>
          </Popup>
        </span>
        {!(!state.valueInvalid || props.disabled) && !props.validationHidden && (
          <div className="value-warn">
            {props.validationMessage || "Zadaná hodnota nemá požadovaný formát"}
          </div>
        )}
      </div>

    </>
  );
});

DateInput.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  steps: PropTypes.object,
  value: PropTypes.instanceOf(Date),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  todayHidden: PropTypes.bool,
  validationHidden: PropTypes.bool,
  validationMessage: PropTypes.string,
  onBlur: PropTypes.func,
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  tabIndex: PropTypes.number,
};

export default DateInput;
